// Headlight `Diagram` input provider — the Excalidraw-backed drawing canvas from pict-section-form,
// wrapped with the shared inline View/Edit presentation behavior (see EditorBase). PSF does
// not auto-register this provider (its heavy editor dep is an opt-in), so the Headlight bundle registers
// it under the hash `Pict-Input-Diagram` that the templates' DefaultInputExtensions reference.
const DiagramInput = require('pict-section-form').DiagramInput;
const { buildEditorInputProvider } = require('./PictProvider-InputType-EditorBase.js');

// The Excalidraw vendor bundle is ~25MB (8.4MB wrapper + 13MB fonts + locales), so it is never bundled
// or script-tagged — pict-section-excalidraw lazy-loads it from these URLs on the FIRST edit only.
// Defaulting them here means every form in every app that attaches this bundle gets a working editor
// with no per-descriptor config. Resolution order (first wins):
//   1. PictForm.Diagram.EditorOptions.LazyLoad*URL  — per field
//   2. provider option ExcalidrawVendorPath         — per app, e.g. attachProviders({'Pict-Input-Diagram': {...}})
//   3. the default below                            — document-relative, matching how headlight-dynamic-form
//                                                     serves its libs (./ alongside index.html)
// The host must serve the contents of
// node_modules/pict-section-excalidraw/vendor/excalidraw-built/ at that path.
const _DEFAULT_VENDOR_PATH = './excalidraw';
const _EDITOR_REACHABLE_TIMEOUT_MS = 5000;
const _UNAVAILABLE_NOTICE_CLASS = 'headlight-diagram-unavailable-notice';

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'Pict-Input-Diagram',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	// Join the app save-data lifecycle so EditorBase.onBeforeSaveDataAsync fires — it flushes an editor
	// left open at save time into FormData (the per-keystroke marshal is deferred to View, so without
	// this a Save-while-editing would persist the pre-edit value). NOT a framework default.
	AutoSaveDataWithApp: true,

	ExcalidrawVendorPath: _DEFAULT_VENDOR_PATH,
};

const EditorProviderBase = buildEditorInputProvider(DiagramInput, _DEFAULT_CONFIGURATION);

class PictProviderInputTypeDiagram extends EditorProviderBase
{
	/**
	 * Point the editor view at the lazily-loaded Excalidraw vendor bundle unless the field or the app
	 * already named its own URLs.
	 *
	 * @param {Record<string, any>} pInput @param {any} pValue @param {Record<string, any>} pInst
	 * @return {Record<string, any>}
	 */
	_buildEditorOptions(pInput, pValue, pInst)
	{
		const tmpOptions = super._buildEditorOptions(pInput, pValue, pInst);
		const tmpVendorPath = (this.options && this.options.ExcalidrawVendorPath) || _DEFAULT_VENDOR_PATH;

		// IFRAME mode is the default here, and that is a Headlight-specific requirement rather than a
		// preference: the form document already loads React 17 as a window global, Excalidraw needs
		// React 18+ (it calls ReactDOM.createRoot), and the react embed only fetches its own bundled
		// React when `window.React` is ABSENT — so in this host it binds to React 17 and dies with
		// "tmpReactDOM.createRoot is not a function". The iframe host loads its own React 18 inside the
		// frame, so the page's React is irrelevant. Switch to 'react' only in a host with React 18+.
		//
		// pict-section-form passes the field's choice as `EditorImplementation`, but the excalidraw
		// module reads `EmbedMode` — so map it here, which is also what makes the descriptor's
		// documented "EditorImplementation" option actually take effect.
		//
		// Read the choice off the DESCRIPTOR, not off the options PSF just built: PSF always writes
		// `EditorImplementation: <field value> || 'react'`, so the key is never absent and testing it
		// would pin every field to react and never reach the iframe default below.
		const tmpDiagramOptions = (pInput && pInput.PictForm && pInput.PictForm.Diagram) || {};
		const tmpRequestedMode = tmpOptions.EmbedMode || tmpDiagramOptions.EditorImplementation;
		const tmpEmbedMode = tmpRequestedMode || 'iframe';
		tmpOptions.EmbedMode = tmpEmbedMode;
		// Keep the two in step so anything reading the PSF-style key sees the mode actually in use.
		tmpOptions.EditorImplementation = tmpEmbedMode;

		if (!tmpOptions.IframeHostURL)
		{
			tmpOptions.IframeHostURL = `${tmpVendorPath}/excalidraw-iframe-host.html`;
		}
		// Fonts + locales. In iframe mode the wrapper runs INSIDE the host page (which already sits in
		// the vendor directory), so the path is relative to that; in react mode it runs in this page.
		if (!tmpOptions.AssetBaseURL)
		{
			tmpOptions.AssetBaseURL = (tmpEmbedMode === 'iframe') ? './assets/' : `${tmpVendorPath}/assets/`;
		}

		// Lazy-load URLs are for REACT mode only. Setting them in iframe mode is not merely redundant:
		// the react view's _lazyLoadVendor() unconditionally injects a <script> for the wrapper, so a
		// stray URL pulls all ~8.4MB of it into the PARENT page — which the iframe host never uses,
		// because it loads its own copy (and its own React) inside the frame.
		if (tmpEmbedMode === 'react')
		{
			// PSF merges PictForm.Diagram.EditorOptions first, so a field-level URL is already present
			// here and must not be clobbered.
			if (!tmpOptions.LazyLoadWrapperURL)
			{
				tmpOptions.LazyLoadWrapperURL = `${tmpVendorPath}/excalidraw-wrapper.min.js`;
			}
			if (!tmpOptions.LazyLoadReactVendorURL)
			{
				tmpOptions.LazyLoadReactVendorURL = `${tmpVendorPath}/react-vendor.min.js`;
			}
		}
		else
		{
			// IFRAME re-seeding. Reopening a saved diagram has to parse the scene back out of the stored
			// SVG, and only the iframe side has the vendor that can do it — the parent's
			// `_extractSceneFromSvg` calls `window.PictSectionExcalidrawVendor`, which is never present in
			// this host (that absence is the whole reason we run in the iframe), so it returns null and
			// the editor opens blank over real content. Hand the raw SVG through as InitialData.svgSource;
			// the iframe host (patched) parses it before mount. `_resolveInitialData` only falls back to
			// InitialData when the AppData scene is empty, which it always is here, so this does not fight
			// the react path. elements:[] is required — an empty array still reads as "no scene yet".
			if (this._isLikelySvg(pValue))
			{
				tmpOptions.InitialData =
				{
					elements: [],
					appState: {},
					files: {},
					svgSource: pValue,
				};
			}
		}
		return tmpOptions;
	}

	/**
	 * An iOS offline form (`window._OfflineMode`) carries no Excalidraw vendor, because the offline capture
	 * keeps only the scripts already on the page, and the iPad app loads that form from a local file even when
	 * online. There, Edit first checks that the editor can load; if it
	 * cannot, the field stays in View with the saved drawing and a note, instead of opening a blank editor.
	 *
	 * @param {string} pInputHash @param {string} pRawHTMLID @param {string} pMode - 'edit' | 'view'
	 */
	setEditorMode(pInputHash, pRawHTMLID, pMode)
	{
		if ((pMode !== 'edit') || (typeof window === 'undefined') || !(/** @type {any} */ (window))._OfflineMode)
		{
			return super.setEditorMode(pInputHash, pRawHTMLID, pMode);
		}
		this._isEditorReachable().then((pReachable) =>
		{
			if (pReachable)
			{
				super.setEditorMode(pInputHash, pRawHTMLID, pMode);
				return;
			}
			this._showEditorUnavailable(pRawHTMLID);
		});
	}

	/**
	 * @return {Promise<boolean>} True when the Excalidraw iframe host page can be fetched right now.
	 */
	_isEditorReachable()
	{
		if ((typeof navigator !== 'undefined') && (navigator.onLine === false))
		{
			return Promise.resolve(false);
		}
		const tmpVendorPath = (this.options && this.options.ExcalidrawVendorPath) || _DEFAULT_VENDOR_PATH;
		let tmpURL;
		try
		{
			tmpURL = new URL(`${tmpVendorPath}/excalidraw-iframe-host.html`, document.baseURI).href;
		}
		catch (pError)
		{
			return Promise.resolve(false);
		}
		const tmpTimeout = new Promise((fResolve) => setTimeout(() => fResolve(false), _EDITOR_REACHABLE_TIMEOUT_MS));
		// The reports service answers a missing asset with 200 and a JSON body, so a JSON reply is a miss.
		const tmpProbe = fetch(tmpURL, { cache: 'no-store' }).then(
			(pResponse) => pResponse.ok && !(pResponse.headers.get('content-type') || '').includes('json'),
			() => false);
		return Promise.race([ tmpProbe, tmpTimeout ]);
	}

	/**
	 * @param {string} pRawHTMLID
	 */
	_showEditorUnavailable(pRawHTMLID)
	{
		const tmpSlot = document.getElementById(this.getContentDisplayHTMLID(pRawHTMLID));
		if (!tmpSlot || tmpSlot.querySelector(`.${_UNAVAILABLE_NOTICE_CLASS}`))
		{
			return;
		}
		const tmpNotice = document.createElement('p');
		tmpNotice.className = `help is-warning ${_UNAVAILABLE_NOTICE_CLASS}`;
		tmpNotice.textContent = 'Drawings can\'t be edited in the iPad app yet. The saved drawing is unchanged.';
		tmpSlot.appendChild(tmpNotice);
	}
}

// NOTE: there is deliberately no "wipe guard" here. An earlier version refused to let a blank export
// overwrite a diagram whose scene could not be re-seeded, paired with a Clear button. Both were removed
// 2026-07-22 (Doug's call): with the export + re-seed fixes in place, a real Excalidraw diagram always
// carries an embedded scene and re-opens with its content, so open→close preserves it on its own. The
// only unseedable values left are hand-authored placeholder SVGs (the examples) — and treating an
// intentional blank (Excalidraw's nav-menu "reset the canvas") as a value worth keeping is the behavior
// Doug wants: what the editor shows on close is what saves, blank included.

module.exports = PictProviderInputTypeDiagram;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
